/**
 * View types - matching the backend views table and API
 */

import type { FilterCondition, FilterLogic, RelativeDate, FilterOperator } from '../columns/types';

// View as returned from the API
export interface View {
  id: string;
  tenant_id: string;
  site_id: string | null;  // null = organization template
  content_type: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  columns: string[];
  filters: ViewFilter[];
  filter_logic: FilterLogic;
  sort_column: string | null;
  sort_direction: 'asc' | 'desc';
  visibility: 'private' | 'shared';
  is_default: boolean;
  created_by: string;
  use_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

// Filter as stored in views (without id, that's added by useFilters)
export interface ViewFilter {
  column: string;
  operator: FilterOperator;
  value: unknown;
  relativeTo?: RelativeDate;
}

// Create view request
export interface CreateViewRequest {
  siteId?: string | null;
  contentType: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  columns: string[];
  filters?: ViewFilter[];
  filterLogic?: FilterLogic;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  visibility?: 'private' | 'shared';
  isDefault?: boolean;
}

// Update view request
export interface UpdateViewRequest {
  name?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  columns?: string[];
  filters?: ViewFilter[];
  filterLogic?: FilterLogic;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  visibility?: 'private' | 'shared';
  isDefault?: boolean;
}

// Response types
export interface ViewsListResponse {
  views: View[];
  templates: View[];
}

export interface ViewResponse {
  view: View;
}

// Convert API filter to FilterCondition (add id for React)
export function viewFilterToCondition(filter: ViewFilter, index: number): FilterCondition {
  return {
    id: `view-filter-${index}-${Date.now()}`,
    column: filter.column,
    operator: filter.operator,
    value: filter.value,
    relativeTo: filter.relativeTo,
  };
}

// Convert FilterCondition to ViewFilter (strip id)
export function conditionToViewFilter(condition: FilterCondition): ViewFilter {
  return {
    column: condition.column,
    operator: condition.operator,
    value: condition.value,
    relativeTo: condition.relativeTo,
  };
}
